/**
 * Documents processed in this browser with their conflict count (#96). The
 * Todo module's placeholder list reads it. Temporary: once analyses are
 * stored and versioned on the server (gateway#138 for own documents too),
 * the Todo module reads from there and this store goes away.
 * Person data: cleared on logout (person-data.ts).
 */

import { STORAGE_KEYS } from './app-constants';
import { documentIdOf } from './document-history';
import type { DocumentSource } from './document-history';
import { logger } from './logger';

export interface ProcessedDocument {
  /** The Recent history id of the same file, so it reopens the same way. */
  id: string;
  name: string;
  size: number;
  type: string;
  lastModified: number;
  source?: DocumentSource;
  conflicts: number;
  processedAt: number;
}

export interface ProcessedInput {
  name: string;
  size: number;
  type: string;
  lastModified: number;
  source?: DocumentSource;
}

export const MAX_PROCESSED_DOCUMENTS = 50;

const isEntry = (value: unknown): value is ProcessedDocument => {
  const entry = value as ProcessedDocument | null;
  return (
    typeof entry?.id === 'string' &&
    typeof entry.name === 'string' &&
    typeof entry.conflicts === 'number' &&
    typeof entry.processedAt === 'number'
  );
};

/** Newest first. */
export function listProcessedDocuments(
  storage: Storage = window.localStorage
): ProcessedDocument[] {
  try {
    const raw = storage.getItem(STORAGE_KEYS.PROCESSED_DOCUMENTS);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isEntry) : [];
  } catch (error) {
    logger.warn('Could not read processed documents:', error);
    return [];
  }
}

/** Processing the same file again replaces its entry and moves it to the top. */
export function recordProcessedDocument(
  document: ProcessedInput,
  conflicts: number,
  now: number = Date.now(),
  storage: Storage = window.localStorage
): void {
  const entry: ProcessedDocument = {
    id: documentIdOf(document),
    name: document.name,
    size: document.size,
    type: document.type,
    lastModified: document.lastModified,
    ...(document.source ? { source: document.source } : {}),
    conflicts,
    processedAt: now,
  };
  const others = listProcessedDocuments(storage).filter(
    (existing) => existing.id !== entry.id
  );
  try {
    storage.setItem(
      STORAGE_KEYS.PROCESSED_DOCUMENTS,
      JSON.stringify([entry, ...others].slice(0, MAX_PROCESSED_DOCUMENTS))
    );
  } catch (error) {
    logger.warn('Could not store the processed document:', error);
  }
}
